import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';
import { PermissionGate, useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
  effectiveDate: string;
  endDate: string | null;
  contactNumber: string;
  emailAddress: string;
  sbu: string;
  customerType?: string;
  assignToUserScope?: string;
  accountOwner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  salesRepresentative: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  fixedHoursBill?: boolean;
  totalFixedHours?: number | null;
}

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatDate, formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = useDynamicPermissions();

  // Check permissions for customers using can function directly
  const canView = can('customers', 'read');

  useEffect(() => {
    // Don't check permissions if they're still loading
    if (permissionsLoading) {
      return;
    }
    
    if (!canView) {
      navigate('/dashboard');
      return;
    }
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/customers/${id}`);
        setCustomer(response.data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch customer');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id, canView, navigate, permissionsLoading]);

  

  const handleDelete = async () => {
    if (!customer) return;
    
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete customer "${customer.accountName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/customers/${id}`);
              toast.success('Customer deleted successfully');
              navigate('/customers');
            } catch (err: any) {
              toast.error(err.response?.data?.error || 'Failed to delete customer');
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm'
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      customUI: ({ onClose, title, message }) => (
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 border border-gray-100">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L3.054 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
            <button
              className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              onClick={onClose}
            >
              Delete
            </button>
          </div>
        </div>
      )
    });
  };

  const getSBUDisplay = (sbu: string) => {
    return sbu; // BU1, BU2, etc. are already display-ready
  };

  const getCustomerTypeDisplay = (customerType?: string) => {
    switch (customerType) {
      case 'CSP':
        return 'CSP';
      case 'NON_CSP':
        return 'Non CSP';
      default:
        return '-';
    }
  };

  const getAssignToUserScopeDisplay = (scope?: string) => {
    switch (scope) {
      case 'CONTACTS_ONLY':
        return 'Customer Contacts Only';
      case 'TENARAI_AND_CONTACTS':
        return 'Tenarai Users and Customer Contacts';
      case 'TENARAI_ONLY':
      default:
        return 'Tenarai Users Only';
    }
  };

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-gray-500">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link
          to="/customers"
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Customers
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">{customer.accountName}</h1>
          <div className="flex gap-2">
            <PermissionGate resource="customers" action="update">
              <Link
                to={`/customers/${id}/edit`}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                Edit
              </Link>
            </PermissionGate>
            <PermissionGate resource="customers" action="delete">
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
              >
                Delete
              </button>
            </PermissionGate>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.accountName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Short Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.accountShortName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Strategic Business Unit</dt>
                  <dd className="mt-1">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                      {getSBUDisplay(customer.sbu)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Customer Type</dt>
                  <dd className="mt-1">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                      {getCustomerTypeDisplay(customer.customerType)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Assign To Users</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {getAssignToUserScopeDisplay(customer.assignToUserScope)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Effective Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(customer.effectiveDate)}</dd>
                </div>
                {customer.endDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">End Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(customer.endDate)}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a href={`mailto:${customer.emailAddress}`} className="text-blue-600 hover:text-blue-800">
                      {customer.emailAddress}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contact Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a href={`tel:${customer.contactNumber}`} className="text-blue-600 hover:text-blue-800">
                      {customer.contactNumber}
                    </a>
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Management</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account Owner</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {customer.accountOwner.firstName} {customer.accountOwner.lastName}
                    <div className="text-xs text-gray-500">{customer.accountOwner.email}</div>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sales Representative</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {customer.salesRepresentative.firstName} {customer.salesRepresentative.lastName}
                    <div className="text-xs text-gray-500">{customer.salesRepresentative.email}</div>
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Configuration</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fixed Hours Bill</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {customer.fixedHoursBill ? 'Yes' : 'No'}
                  </dd>
                </div>
                {customer.fixedHoursBill && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Fixed Hours</dt>
                    <dd className="mt-1 text-sm text-gray-900">{customer.totalFixedHours ?? 0}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(customer.createdAt)}</dd>
                </div>
                {customer.createdBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created By</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {customer.createdBy.firstName} {customer.createdBy.lastName}
                      <div className="text-xs text-gray-500">{customer.createdBy.email}</div>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(customer.updatedAt)}</dd>
                </div>
                {customer.updatedBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Updated By</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {customer.updatedBy.firstName} {customer.updatedBy.lastName}
                      <div className="text-xs text-gray-500">{customer.updatedBy.email}</div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;